import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTeamStore } from '../../lib/store/teamStore';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { supabase } from '../../lib/supabase/client';
import { PreferenceStatusCard } from '@/components/teams/PreferenceStatusCard';
import { useTeamSchedules } from '../../hooks/useSchedule';
import { format, parseISO } from 'date-fns';
import ScheduleStatusBadge from '../../components/schedule/ScheduleStatusBadge';
import { ScheduleStatus } from '../../types/schedule';

export const TeamDashboardPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { currentTeam, isLoading, error, setCurrentTeam } = useTeamStore();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [roleLoading, setRoleLoading] = useState<boolean>(true);
  const { schedules, isLoading: isLoadingSchedules } = useTeamSchedules(teamId);
  
  useEffect(() => {
    if (teamId) {
      setCurrentTeam(teamId);
    }
  }, [teamId, setCurrentTeam]);
  
  // Check if the user is an admin
  useEffect(() => {
    const checkUserRole = async () => {
      if (!teamId || !supabase) return;
      
      try {
        setRoleLoading(true);
        
        // Get current user
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
          setIsAdmin(false);
          setRoleLoading(false);
          return;
        }
        
        // Check if user is an admin
        const { data: member, error: memberError } = await supabase
          .from('team_members')
          .select('role')
          .eq('team_id', teamId)
          .eq('user_id', userData.user.id)
          .single();
        
        if (memberError) {
          console.error('Error checking team role:', memberError);
          setIsAdmin(false);
        } else {
          setIsAdmin(member?.role === 'admin');
        }
      } catch (err) {
        console.error('Error checking team role:', err);
        setIsAdmin(false);
      } finally {
        setRoleLoading(false);
      }
    };
    
    checkUserRole();
  }, [teamId]);
  
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return dateString;
    }
  };
  
  if (isLoading || roleLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      </div>
    );
  }
  
  if (!currentTeam) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert variant="error" className="mb-4">
          Team not found
        </Alert>
      </div>
    );
  }
  
  // Get maximum 3 recent schedules to display
  const recentSchedules = schedules ? schedules.slice(0, 3) : [];
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{currentTeam.name}</h1>
        <div className="flex space-x-4">
          {isAdmin && (
            <>
              <Link to={`/teams/${teamId}/settings`}>
                <Button variant="outline">Team Settings</Button>
              </Link>
              <Link to={`/teams/${teamId}/members/invite`}>
                <Button variant="primary">Invite Members</Button>
              </Link>
            </>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-2">Staffing Requirements</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">Day Providers:</span>
              <span className="font-medium">{currentTeam.day_providers_required}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Night Providers:</span>
              <span className="font-medium">{currentTeam.night_providers_required}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Shifts Per Provider:</span>
              <span className="font-medium">{currentTeam.shifts_per_provider}</span>
            </div>
          </div>
          {isAdmin && (
            <div className="mt-4">
              <Link to={`/teams/${teamId}/settings/staffing`}>
                <Button variant="text" size="sm">Edit Requirements</Button>
              </Link>
            </div>
          )}
        </Card>
        
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-2">Scheduling Rules</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">Period Length:</span>
              <span className="font-medium">{currentTeam.scheduling_period_days} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Max Consecutive:</span>
              <span className="font-medium">{currentTeam.max_consecutive_shifts} shifts</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Min Rest:</span>
              <span className="font-medium">{currentTeam.min_rest_hours} hours</span>
            </div>
          </div>
          {isAdmin && (
            <div className="mt-4">
              <Link to={`/teams/${teamId}/settings/scheduling`}>
                <Button variant="text" size="sm">Edit Rules</Button>
              </Link>
            </div>
          )}
        </Card>
        
        <PreferenceStatusCard />
        
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-2">Quick Actions</h3>
          <div className="space-y-3">
            {isAdmin && (
              <Link to={`/teams/${teamId}/schedules/new`}>
                <Button variant="secondary" className="w-full">
                  Create New Schedule
                </Button>
              </Link>
            )}
            <Link to={`/teams/${teamId}/members`}>
              <Button variant="secondary" className="w-full">
                View Team Members
              </Button>
            </Link>
            {isAdmin && (
              <Link to={`/teams/${teamId}/shift-types`}>
                <Button variant="secondary" className="w-full">
                  Manage Shift Types
                </Button>
              </Link>
            )}
            <Link to={`/teams/${teamId}/schedules`}>
              <Button variant="secondary" className="w-full">
                View All Schedules
              </Button>
            </Link>
          </div>
        </Card>
      </div>
      
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Schedules</h2>
        {isLoadingSchedules ? (
          <div className="flex justify-center py-4">
            <Spinner size="sm" />
          </div>
        ) : recentSchedules.length > 0 ? (
          <div className="space-y-4">
            {recentSchedules.map((schedule) => (
              <div 
                key={schedule.id} 
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
              >
                <div>
                  <div className="font-medium">
                    {formatDate(schedule.start_date)} - {formatDate(schedule.end_date)}
                  </div>
                  <div className="flex items-center mt-1">
                    <ScheduleStatusBadge status={schedule.status as ScheduleStatus} />
                  </div>
                </div>
                <Link
                  to={`/teams/${teamId}/schedules/${schedule.id}`}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  View
                </Link>
              </div>
            ))}
            <div className="pt-2 text-center">
              <Link to={`/teams/${teamId}/schedules`}>
                <Button variant="text">View All Schedules</Button>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center py-8 text-gray-500">
              No schedules found. Create your first schedule to get started.
            </div>
            {isAdmin && (
              <div className="flex justify-center">
                <Link to={`/teams/${teamId}/schedules/new`}>
                  <Button variant="primary">Create New Schedule</Button>
                </Link>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}; 